import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  CloseOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import reduce from 'lodash/reduce';
import uniqBy from 'lodash/uniqBy';
import * as yup from 'yup';

import { AutoCompleteForm } from '@/components/form/AutoComplete';
import { InputForm } from '@/components/form/Input';
import { InputNumberForm } from '@/components/form/InputNumber';
import { useAppDispatch, useAppSelector } from '@/core/redux/hooks';
import { airExchangeTableSelectors } from '@/core/redux/slices/airExchangeTable/selectors';
import {
  airExchangeTableActions,
  AirExchangeTableItem,
} from '@/core/redux/slices/airExchangeTable/slice';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';
import { AirExchangePage } from '@/views/airExchange/AirExchangePage/AirExchangePage';

import { ConsumersComponent } from './ConsumersComponent';
import { LinksModal } from './LinksModal';
import { LocalExhaustsTable } from './LocalExhaustsTable';

const { Text, Title } = Typography;

interface IModalData {
  open: boolean;
  type: string;
  saveData: (value: string[]) => void;
  rooms: string[];
}

const generateUniqueId = () => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hexString = Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  return `${hexString.slice(0, 8)}-${hexString.slice(8, 12)}-${hexString.slice(12, 16)}-${hexString.slice(16, 20)}-${hexString.slice(20)}`;
};

const AddRoom: React.FC = () => {
  const [isOpenDirectory, setOpenDirectory] = useState<boolean>(false);
  const [isOpenMOTable, setOpenMOTable] = useState<boolean>(false);
  const [modalLinksData, setModalLinksData] = useState<IModalData | null>();
  const [exhaust_linked, setExhaust_linked] = useState<string[]>([]);
  const [inflow_linked, setInflow_linked] = useState<string[]>([]);
  const [similar_rooms, setSimilar_rooms] = useState<string[]>([]);

  const { isRender, data } = useAppSelector(
    airExchangeTableSelectors.addRoomModal
  );

  const isSending = useAppSelector(airExchangeTableSelectors.isSending);
  const system = useAppSelector(systemSelectors.system);

  const dispatch = useAppDispatch();

  const schema = yup.object().shape({
    num: yup.string().required('Это поле обязательно для заполнения'),
    name: yup.string().required('Это поле обязательно для заполнения'),
    area: yup.number().required('Это поле обязательно для заполнения'),
    height: yup.number().required('Это поле обязательно для заполнения'),
    volume: yup.number().required('Это поле обязательно для заполнения'),
  });

  const methods = useForm<AirExchangeTableItem>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      people_count: null,
      multiplicity_inflow: null,
      multiplicity_exhaust: null,
      flow_inflow: null,
      flow_exhaust: null,
      flow_mo_inflow: null,
      flow_mo_exhaust: null,
      sys_inflow: null,
      sys_exhaust: null,
      sys_mo_inflow: null,
      sys_mo_exhaust: null,
      note: null,
      inflow_consumers: data
        ? null
        : [
            {
              consumer_count: 0,
              flow_per_consumer: 0,
            },
          ],
      exhaust_consumers: null,
      inflow_linked: null,
      exhaust_linked: null,
      mo_table: null,
    },
  });

  const area = methods.watch('area');
  const height = methods.watch('height');
  const multiplicity_inflow = methods.watch('multiplicity_inflow');
  const multiplicity_exhaust = methods.watch('multiplicity_exhaust');
  const volume = methods.watch('volume');
  const roomId = methods.watch('id');

  useEffect(() => {
    if (area && height) {
      const resultValue = area * height;
      methods.setValue('volume', Number(resultValue.toFixed(1)));
    } else {
      methods.setValue('volume', 0);
    }
  }, [area, height]);

  useEffect(() => {
    if (data) {
      if (data?.mo_table?.length) setOpenMOTable(true);
      methods.reset(data);

      if (!data?.inflow_linked && !data?.exhaust_linked) {
        const result_inflow_linked: string[] = [];
        const result_exhaust_linked: string[] = [];
        system?.state?.table?.map((el: any) => {
          if (el?.id !== data?.id && el?.inflow_linked === data?.id) {
            result_inflow_linked.push(el?.id);
          }
          if (el?.id !== data?.id && el?.exhaust_linked === data?.id) {
            result_exhaust_linked.push(el?.id);
          }
        });
        if (result_inflow_linked?.length) {
          setInflow_linked(result_inflow_linked);
        }
        if (result_exhaust_linked?.length) {
          setExhaust_linked(result_exhaust_linked);
        }
      }
    } else {
      methods.setValue('id', generateUniqueId());
      methods.setValue('type_balance', 'Приток равен вытяжке');
      handleRadioInflow('По людям');
    }

    return () => {
      setInflow_linked([]);
      setExhaust_linked([]);
    };
  }, [data]);

  const submit = () => {
    dispatch(airExchangeTableActions.setIsSending(true));
    const newTable = system?.state ? [...system?.state?.table] : [];
    if (methods?.getValues('type_balance') === 'Приток равен вытяжке') {
      methods.setValue(
        'flow_exhaust',
        Math.ceil((methods?.getValues('flow_inflow') || 0) / 5) * 5 || null
      );
      methods.setValue(
        'multiplicity_exhaust',
        methods?.getValues('multiplicity_inflow')
      );

      methods.setValue(
        'exhaust_consumers',
        methods?.getValues('inflow_consumers')
      );
    }

    const getSum = (arr: string[], key: string) => {
      const resultCount = newTable.reduce((acc, el) => {
        if (arr?.includes(el?.id)) {
          acc +=
            key === 'flow_inflow'
              ? el[key] - el?.flow_exhaust || 0
              : key === 'flow_exhaust'
                ? el[key] - el?.flow_inflow
                : 0;
        }
        return acc;
      }, 0);
      return resultCount ? Math.ceil(resultCount / 5) * 5 : null;
    };

    if (methods?.getValues('type_inflow') === 'По ссылке на вытяжку') {
      methods.setValue('flow_inflow', getSum(exhaust_linked, 'flow_exhaust'));
    } else if (methods?.getValues('type_inflow') === 'По людям') {
    }

    if (methods?.getValues('type_exhaust') === 'По ссылке на приток') {
      methods.setValue('flow_exhaust', getSum(inflow_linked, 'flow_inflow'));
    }

    const inflowLinkId = methods?.getValues('inflow_linked');
    const sumInflowLinks =
      newTable?.reduce((acc, el) => {
        if (el?.inflow_linked === inflowLinkId && el?.id !== roomId) {
          acc +=
            el?.flow_inflow - el?.flow_exhaust > 0
              ? el?.flow_inflow - el?.flow_exhaust
              : 0;
        }
        return acc;
      }, 0) || null;

    const exhaustLinkId = methods?.getValues('exhaust_linked');
    const sumExhaustLinks =
      newTable?.reduce((acc, el) => {
        if (el?.exhaust_linked === exhaustLinkId && el?.id !== roomId) {
          acc +=
            el?.flow_exhaust - el?.flow_inflow > 0
              ? el?.flow_exhaust - el?.flow_inflow
              : 0;
        }
        return acc;
      }, 0) || null;

    const roomData = methods?.getValues();

    if (!data) {
      newTable.push(roomData);
    } else {
      const foundInd = newTable?.findIndex((el) => el?.id === data?.id);
      newTable?.splice(foundInd, 1, methods?.getValues());
    }

    const result = newTable?.map((el: any) => {
      const itemData = { ...el };

      if (inflow_linked?.includes(el?.id)) {
        itemData.inflow_linked = roomId;
      } else if (
        !inflow_linked?.includes(el?.id) &&
        el?.inflow_linked === roomId
      ) {
        itemData.inflow_linked = null;
      }

      if (exhaust_linked?.includes(el?.id)) {
        itemData.exhaust_linked = roomId;
      } else if (
        !exhaust_linked?.includes(el?.id) &&
        el?.exhaust_linked === roomId
      ) {
        itemData.exhaust_linked = null;
      }

      if (inflowLinkId !== null && inflowLinkId === itemData?.id) {
        itemData.flow_exhaust =
          sumInflowLinks +
          (Number(roomData?.flow_inflow) - Number(roomData?.flow_exhaust));
      }

      if (exhaustLinkId !== null && exhaustLinkId === itemData?.id) {
        itemData.flow_inflow = sumExhaustLinks;
        itemData.flow_inflow =
          sumExhaustLinks +
          (Number(roomData?.flow_exhaust) - Number(roomData?.flow_inflow));
      }

      if (similar_rooms?.includes(el?.id)) {
        itemData.flow_inflow = roomData?.flow_inflow;
        itemData.flow_exhaust = roomData?.flow_exhaust;
        itemData.sys_inflow = roomData?.sys_inflow;
        itemData.sys_exhaust = roomData?.sys_exhaust;
        itemData.people_count = roomData?.people_count;
        itemData.inflow_consumers = roomData?.inflow_consumers;
        itemData.exhaust_consumers = roomData?.exhaust_consumers;
        itemData.mo_table = roomData?.mo_table;
        itemData.type_balance = roomData?.type_balance;
        itemData.type_inflow = roomData?.type_inflow;
        itemData.type_exhaust = roomData?.type_exhaust;
        itemData.multiplicity_inflow = roomData?.multiplicity_inflow;
        itemData.multiplicity_exhaust = roomData?.multiplicity_exhaust;

        if (itemData?.type_inflow === 'По расходу') {
          itemData.flow_inflow = roomData?.flow_inflow;
        }

        if (itemData?.type_exhaust === 'По расходу') {
          itemData.flow_exhaust = roomData?.flow_exhaust;
        }

        if (itemData?.type_inflow === 'По кратности') {
          if (itemData?.volume && itemData?.multiplicity_inflow) {
            itemData.flow_inflow =
              Math.ceil(
                (itemData?.volume * itemData?.multiplicity_inflow) / 5
              ) * 5;
          }
        }

        if (itemData?.type_exhaust === 'По кратности') {
          if (itemData?.volume && itemData?.multiplicity_exhaust) {
            itemData.flow_exhaust =
              Math.ceil(
                (itemData?.volume * itemData?.multiplicity_exhaust) / 5
              ) * 5;
          }
        }

        if (itemData?.type_inflow === 'По людям') {
          const resultData = reduce(
            itemData.inflow_consumers,
            (a: any, b: any) => {
              return a + b?.consumer_count * b?.flow_per_consumer;
            },
            0
          );
          itemData.flow_inflow = Math.ceil(resultData / 5) * 5;
        }

        if (itemData?.type_exhaust === 'По потребителю') {
          const resultData = reduce(
            itemData.exhaust_consumers,
            (a: any, b: any) => {
              return a + b?.consumer_count * b?.flow_per_consumer;
            },
            0
          );
          itemData.flow_exhaust = Math.ceil(resultData / 5) * 5;
        }
      }

      return itemData;
    });

    system?.id &&
      dispatch(
        airExchangeTableActions.performAirExchangeTable({
          calc_id: system?.id,
          data: { table: result },
        })
      );
  };

  const floorOptions: any = uniqBy(
    system?.state?.table
      ?.map((el: any) => {
        return { value: el?.floor, label: el?.floor };
      })
      ?.filter((el: any) => el?.value !== null),
    'value'
  );

  const blockOptions: any = uniqBy(
    system?.state?.table
      ?.map((el: any) => {
        return { value: el?.block, label: el?.block };
      })
      ?.filter((el: any) => el?.value !== null),
    'value'
  );

  const sys_inflow_options: any = uniqBy(
    system?.state?.table
      ?.map((el: any) => {
        return { value: el?.sys_inflow, label: el?.sys_inflow };
      })
      ?.filter((el: any) => el?.value !== null),
    'value'
  );

  const sys_exhaust_options: any = uniqBy(
    system?.state?.table
      ?.map((el: any) => {
        return { value: el?.sys_exhaust, label: el?.sys_exhaust };
      })
      ?.filter((el: any) => el?.value !== null),
    'value'
  );

  const handleMOTable = (open: boolean) => {
    const emptyRow = {
      i: 0,
      count: null,
      flow_mo_inflow: null,
      flow_mo_exhaust: null,
      flow_inflow: null,
      flow_exhaust: null,
      sys_mo_inflow: null,
      sys_mo_exhaust: null,
    };

    methods.setValue('mo_table', open ? [emptyRow] : null);

    setOpenMOTable(open);
  };

  useEffect(() => {
    if (methods?.getValues('type_inflow') === 'По кратности') {
      if (volume && multiplicity_inflow) {
        methods.setValue(
          'flow_inflow',
          Math.ceil((volume * multiplicity_inflow) / 5) * 5
        );
      } else {
        methods.setValue('flow_inflow', null);
      }
    }

    if (methods?.getValues('type_exhaust') === 'По кратности') {
      if (volume && multiplicity_exhaust) {
        methods.setValue(
          'flow_exhaust',
          Math.ceil((volume * multiplicity_exhaust) / 5) * 5
        );
      } else {
        methods.setValue('flow_exhaust', null);
      }
    }
  }, [multiplicity_inflow, multiplicity_exhaust]);

  const balanceOptions = [
    {
      label: (
        <div className='pt-2 pb-2'>
          <div className='text-2xl font-semibold gap-2 flex justify-center'>
            <span className='text-[#F5222D]'> П</span>=
            <span className='text-[#1890FF]'>В</span>
          </div>
          <div className='leading-5'>
            Приток равен <br /> вытяжке
          </div>
        </div>
      ),
      value: 'Приток равен вытяжке',
    },
    {
      label: (
        <div className='pt-2 pb-2'>
          <div className='text-2xl font-semibold gap-2 flex justify-center'>
            <span className='text-[#F5222D]'> П</span> ≠
            <span className='text-[#1890FF]'>В</span>
          </div>
          <div className='leading-5'>
            Приток не равен <br /> вытяжке
          </div>
        </div>
      ),
      value: 'Приток не равен вытяжке',
    },
    {
      label: (
        <div className='pt-2 pb-2'>
          <div className='text-[#1890FF] text-2xl font-semibold'>В</div>
          <div className='leading-5'>
            Только <br /> вытяжка
          </div>
        </div>
      ),
      value: 'Только вытяжка',
    },
    {
      label: (
        <div className='pt-2 pb-2'>
          <div className='text-[#F5222D] text-2xl font-semibold'> П</div>
          <div className='leading-5'>
            Только <br /> приток
          </div>
        </div>
      ),
      value: 'Только приток',
    },
  ];

  const style: React.CSSProperties = {
    flexGrow: '1',
    height: 'fit-content',
    whiteSpace: 'nowrap',
    lineHeight: '1.25rem',
    padding: '0.5rem',
  };

  const radioInflowOptions: CheckboxGroupProps<string>['options'] = [
    {
      label: 'По людям',
      value: 'По людям',
      style: style,
    },
    {
      label: 'По кратности',
      value: 'По кратности',
      style: style,
    },
    {
      label: 'По расходу',
      value: 'По расходу',
      style: style,
    },
  ];

  const radioExhaustOptions: CheckboxGroupProps<string>['options'] = [
    {
      label: 'По потребителю',
      value: 'По потребителю',
      style: style,
    },
    {
      label: 'По кратности',
      value: 'По кратности',
      style: style,
    },
    {
      label: 'По расходу',
      value: 'По расходу',
      style: style,
    },
  ];

  if (methods?.getValues('type_balance') === 'Только вытяжка') {
    radioExhaustOptions.push({
      label: (
        <div
          className='p-[0.5rem]'
          onClick={() =>
            setModalLinksData({
              open: true,
              type: 'inflow_linked',
              saveData: setInflow_linked,
              rooms: inflow_linked,
            })
          }
        >
          По ссылке на приток
        </div>
      ),
      value: 'По ссылке на приток',
      style: { ...style, padding: '0' },
    });
  }

  if (methods?.getValues('type_balance') === 'Только приток') {
    radioInflowOptions.push({
      label: (
        <div
          className='p-[0.5rem]'
          onClick={() =>
            setModalLinksData({
              open: true,
              type: 'exhaust_linked',
              saveData: setExhaust_linked,
              rooms: exhaust_linked,
            })
          }
        >
          По ссылке на вытяжку
        </div>
      ),
      value: 'По ссылке на вытяжку',
      style: { ...style, padding: '0' },
    });
  }

  const closeModal = () => {
    dispatch(
      airExchangeTableActions.setAddRoomModal({
        isRender: false,
      })
    );
  };

  const handleRadioInflow = (value: string) => {
    methods.setValue('flow_inflow', null);
    methods.setValue('multiplicity_inflow', null);
    setInflow_linked([]);

    if (value === 'По людям') {
      methods.setValue('inflow_consumers', [
        {
          consumer_count: 0,
          flow_per_consumer: 0,
        },
      ]);
      if (methods.getValues('type_balance') === 'Приток равен вытяжке') {
        methods.setValue('type_exhaust', 'По потребителю');
      }
    } else {
      methods.setValue('inflow_consumers', null);
      methods.setValue('people_count', null);
      if (methods.getValues('type_balance') === 'Приток равен вытяжке') {
        methods.setValue('type_exhaust', value);
      }
    }
    methods.setValue('type_inflow', value);
  };

  const handleRadioExhaust = (value: string) => {
    methods.setValue('flow_exhaust', null);
    methods.setValue('multiplicity_exhaust', null);
    setExhaust_linked([]);
    if (value === 'По потребителю') {
      methods.setValue('exhaust_consumers', [
        {
          consumer_count: 0,
          flow_per_consumer: 0,
        },
      ]);
    } else {
      methods.setValue('exhaust_consumers', null);
      if (methods?.getValues('type_inflow') !== 'По людям') {
        methods.setValue('people_count', null);
      }
    }

    methods.setValue('type_exhaust', value);
  };

  const handleBalance = (e: string) => {
    if (e === 'Приток не равен вытяжке') {
      handleRadioInflow('По людям');
      handleRadioExhaust('По потребителю');
    }

    if (e === 'Только вытяжка') {
      methods.setValue('type_inflow', null);
      handleRadioExhaust('По потребителю');
      methods.setValue('inflow_consumers', null);
    }

    if (e === 'Только приток') {
      methods.setValue('type_exhaust', null);
      handleRadioInflow('По людям');
      methods.setValue('exhaust_consumers', null);
    }

    methods.setValue('flow_inflow', null);
    methods.setValue('multiplicity_inflow', null);
    methods.setValue('flow_exhaust', null);
    methods.setValue('multiplicity_exhaust', null);
    methods.setValue('sys_inflow', null);
    methods.setValue('sys_exhaust', null);
    methods.setValue('people_count', null);

    setInflow_linked([]);
    setExhaust_linked([]);

    methods.setValue('type_balance', e);
  };

  const getWidth = window.innerWidth - 100;

  const handleChange = (value: number, type: any) => {
    if (value?.toString()?.includes(',') || value?.toString()?.includes('.')) {
      const separator = value?.toString()?.includes(',') ? ',' : '.';
      const arrStr = value?.toString()?.split(separator);
      methods.setValue(
        type,
        Number(`${arrStr[0]}${separator}${arrStr[1]?.substring(0, 2)}`)
      );
    } else {
      methods.setValue(type, value);
    }
  };

  return (
    <Modal
      open={isRender}
      closable={false}
      cancelButtonProps={{ className: 'm-5 text-black' }}
      footer={null}
      maskClosable={false}
      width={isOpenDirectory ? getWidth : 600}
    >
      <div className={isOpenDirectory ? 'flex flex-row' : ''}>
        <FormProvider {...methods}>
          <div
            className={`flex flex-col gap-4 ${isOpenDirectory ? 'min-w-[552px]' : ''}`}
          >
            <div className='flex items-center justify-between'>
              <div className='font-bold text-base'>
                {data ? 'Редактировать помещение' : 'Добавить помещение'}
              </div>
              <Popconfirm
                title='Вы уверены, что хотите закрыть окно?'
                description='Введённые данные в этом окне не будут сохранены.'
                okText='Закрыть окно'
                cancelText='Отменить'
                placement='bottom'
                onConfirm={closeModal}
              >
                <Button type='text' className='text-black p-2'>
                  <CloseOutlined />
                </Button>
              </Popconfirm>
            </div>
            <div className='flex gap-4 items-end'>
              <AutoCompleteForm
                label='Корпус/Отсек или иное'
                name='block'
                options={blockOptions}
              />

              <AutoCompleteForm
                label='Этаж/Уровень'
                name='floor'
                options={floorOptions}
              />
            </div>

            <InputForm name='num' label='Номер помещения' />

            <div className='flex items-start gap-4'>
              <InputForm name='name' label='Наименование помещения' />
              <Button
                className='mt-[26px]'
                onClick={() => setOpenDirectory(!isOpenDirectory)}
              >
                Справочник{' '}
                {isOpenDirectory ? <LeftOutlined /> : <RightOutlined />}
              </Button>
            </div>

            <div className='flex gap-4'>
              <InputNumberForm
                size='middle'
                name='area'
                label='Площадь помещения'
                addonBefore={<i>S =</i>}
                addonAfter={<i>м²</i>}
                fixValue={2}
                onChange={(e: any) => handleChange(e, 'area')}
              />
              <InputNumberForm
                size='middle'
                name='height'
                label={
                  <div>
                    Высота помещения{' '}
                    <Tooltip
                      rootClassName='min-w-fit'
                      title={
                        <div className='text-white'>
                          Для производств при высоте ≥6 м - принимать 6 м.
                          <br />
                          Для жилых/общественных зданий при высоте ≥4 м -
                          принимать 4 м. <br />
                          Согласно п. Г2 СП 60.13330.2020. [
                          <Button
                            type='link'
                            className='p-0 h-fit'
                            onClick={() =>
                              window.open(
                                'https://aeropro.wiki/index.php/%D0%A1%D0%9F_60.13330.2020_%D0%9E%D1%82%D0%BE%D0%BF%D0%BB%D0%B5%D0%BD%D0%B8%D0%B5,_%D0%B2%D0%B5%D0%BD%D1%82%D0%B8%D0%BB%D1%8F%D1%86%D0%B8%D1%8F_%D0%B8_%D0%BA%D0%BE%D0%BD%D0%B4%D0%B8%D1%86%D0%B8%D0%BE%D0%BD%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5_%D0%98%D0%B7%D0%BC_5#%D0%9F%D1%80%D0%B8%D0%BB%D0%BE%D0%B6%D0%B5%D0%BD%D0%B8%D0%B5_%D0%93._%D0%A0%D0%B0%D1%81%D1%87%D0%B5%D1%82_%D1%80%D0%B0%D1%81%D1%85%D0%BE%D0%B4%D0%B0_%D0%BF%D1%80%D0%B8%D1%82%D0%BE%D1%87%D0%BD%D0%BE%D0%B3%D0%BE_%D0%B2%D0%BE%D0%B7%D0%B4%D1%83%D1%85%D0%B0_%D0%B2_%D1%86%D0%B5%D0%BD%D1%82%D1%80%D0%B0%D0%BB%D1%8C%D0%BD%D1%8B%D1%85_%D1%81%D0%B8%D1%81%D1%82%D0%B5%D0%BC%D0%B0%D1%85_%D0%B2%D0%B5%D0%BD%D1%82%D0%B8%D0%BB%D1%8F%D1%86%D0%B8%D0%B8_%D0%B8_%D0%BA%D0%BE%D0%BD%D0%B4%D0%B8%D1%86%D0%B8%D0%BE%D0%BD%D0%B8%D1%80%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D1%8F_%D0%B2%D0%BE%D0%B7%D0%B4%D1%83%D1%85%D0%B0'
                              )
                            }
                          >
                            Подробнее в Аэропро.ВИКИ
                          </Button>
                          ]
                        </div>
                      }
                    >
                      <InfoCircleOutlined style={{ color: 'blue' }} />
                    </Tooltip>
                  </div>
                }
                addonBefore={<i>H =</i>}
                addonAfter={<i>м</i>}
                fixValue={2}
                onChange={(e: any) => handleChange(e, 'height')}
              />
            </div>

            <InputNumberForm
              disabled
              name='volume'
              label='Объем помещения'
              addonBefore={<i>V =</i>}
              addonAfter={<i>м³</i>}
            />

            <InputForm
              name='note'
              label='Примечание'
              placeholder='Введите значение'
            />

            <Text>Баланс</Text>

            <Segmented
              options={balanceOptions}
              onChange={handleBalance}
              defaultValue='Приток равен вытяжке'
              value={methods?.getValues('type_balance') || undefined}
              block
            />

            {[
              'Приток равен вытяжке',
              'Приток не равен вытяжке',
              'Только приток',
            ]?.includes(methods?.getValues('type_balance') || 'null') && (
              <>
                <Text>
                  Расход
                  {['Приток не равен вытяжке', 'Только приток']?.includes(
                    methods?.getValues('type_balance') || 'null'
                  )
                    ? ' притока'
                    : ''}
                </Text>

                <Radio.Group
                  options={radioInflowOptions}
                  className='font-normal flex text-center'
                  buttonStyle='solid'
                  optionType='button'
                  onChange={(e) => handleRadioInflow(e?.target?.value)}
                  value={methods?.getValues('type_inflow')}
                />

                {methods?.getValues('type_inflow') === 'По кратности' && (
                  <div className='flex gap-4'>
                    <div className='w-full'>
                      <InputNumberForm
                        size='middle'
                        name='multiplicity_inflow'
                        label='Кратность'
                        placeholder='Введите значение'
                        onChange={(e: any) =>
                          handleChange(e, 'multiplicity_inflow')
                        }
                      />
                    </div>
                    <InputForm
                      disabled
                      name='flow_inflow'
                      label='Расход воздуха'
                      addonBefore={<i>L =</i>}
                      addonAfter={<i>м³/час</i>}
                    />
                  </div>
                )}

                {methods?.getValues('type_inflow') === 'По расходу' && (
                  <div>
                    <InputNumberForm
                      size='middle'
                      name='flow_inflow'
                      label='Расход воздуха'
                      addonBefore={<i>L =</i>}
                      addonAfter={<i>м³/час</i>}
                      onChange={(e: any) => handleChange(e, 'flow_inflow')}
                    />
                  </div>
                )}

                {methods?.getValues('type_inflow') === 'По людям' && (
                  <ConsumersComponent type='inflow_consumers' />
                )}
              </>
            )}

            {['Приток не равен вытяжке', 'Только вытяжка']?.includes(
              methods?.getValues('type_balance') || 'null'
            ) && (
              <>
                <Text>Расход вытяжки</Text>

                <Radio.Group
                  options={radioExhaustOptions}
                  value={methods?.getValues('type_exhaust')}
                  className='font-normal flex text-center'
                  buttonStyle='solid'
                  optionType='button'
                  onChange={(e) => handleRadioExhaust(e?.target?.value)}
                />

                {methods?.getValues('type_exhaust') === 'По потребителю' && (
                  <ConsumersComponent type='exhaust_consumers' />
                )}

                {methods?.getValues('type_exhaust') === 'По кратности' && (
                  <div className='flex gap-4'>
                    <div className='w-full'>
                      <InputNumberForm
                        size='middle'
                        name='multiplicity_exhaust'
                        label='Кратность'
                        placeholder='Введите значение'
                        onChange={(e: any) =>
                          handleChange(e, 'multiplicity_exhaust')
                        }
                      />
                    </div>
                    <InputForm
                      disabled
                      name='flow_exhaust'
                      label='Расход воздуха'
                      addonBefore={<i>L =</i>}
                      addonAfter={<i>м³/час</i>}
                    />
                  </div>
                )}

                {methods?.getValues('type_exhaust') === 'По расходу' && (
                  <div>
                    <InputNumberForm
                      size='middle'
                      name='flow_exhaust'
                      label='Расход воздуха'
                      addonBefore={<i>L =</i>}
                      addonAfter={<i>м³/час</i>}
                      onChange={(e: any) => handleChange(e, 'flow_exhaust')}
                    />
                  </div>
                )}
              </>
            )}

            {methods?.getValues('type_balance') !== 'Только вытяжка' && (
              <AutoCompleteForm
                label='Обозначение приточной системы'
                name='sys_inflow'
                options={sys_inflow_options}
              />
            )}

            {methods?.getValues('type_balance') !== 'Только приток' && (
              <AutoCompleteForm
                label='Обозначение вытяжной системы'
                name='sys_exhaust'
                options={sys_exhaust_options}
              />
            )}

            <div className='flex justify-between'>
              В помещении имеются местные отсосы (М.О.)
              <Switch checked={isOpenMOTable} onChange={handleMOTable} />
            </div>
            {isOpenMOTable && <LocalExhaustsTable />}

            {!data?.inflow_linked && !data?.exhaust_linked && (
              <Button
                className='w-fit'
                onClick={() =>
                  setModalLinksData({
                    open: true,
                    type: 'similar_rooms',
                    saveData: setSimilar_rooms,
                    rooms: similar_rooms,
                  })
                }
              >
                Однотипные помещения
              </Button>
            )}

            <Divider className='m-0' />

            <div className='flex gap-2 align-stretch'>
              <Popconfirm
                title='Вы уверены, что хотите закрыть окно?'
                description='Введённые данные в этом окне не будут сохранены.'
                okText='Закрыть окно'
                cancelText='Отменить'
                placement='bottom'
                onConfirm={closeModal}
              >
                <Button className='grow'>Назад</Button>
              </Popconfirm>

              <Button
                disabled={!methods.formState.isValid || isSending}
                className='grow'
                onClick={submit}
              >
                {isSending && <Spin indicator={<LoadingOutlined spin />} />}
                Сохранить изменения
              </Button>
            </div>
          </div>
        </FormProvider>
        {isOpenDirectory && (
          <>
            <Divider type='vertical' className='h-auto ml-5 mr-5' />
            <div>
              <div className='flex justify-end'>
                <Tooltip title='Закрыть справочник'>
                  <Button
                    type='text'
                    className='text-black p-2'
                    onClick={() => setOpenDirectory(!isOpenDirectory)}
                  >
                    <CloseOutlined />
                  </Button>
                </Tooltip>
              </div>
              <AirExchangePage />
            </div>
          </>
        )}
      </div>
      {modalLinksData?.open && (
        <LinksModal
          open={modalLinksData?.open}
          setOpen={setModalLinksData}
          type={modalLinksData?.type}
          saveData={modalLinksData?.saveData}
          roomId={roomId}
          currentRooms={modalLinksData?.rooms}
        />
      )}
    </Modal>
  );
};

export const AddRoomModal: React.FC = () => {
  const { isRender } = useAppSelector(airExchangeTableSelectors.addRoomModal);

  if (isRender) {
    return <AddRoom />;
  }

  return null;
};
